/* --------------------------------------------------------------------------
    PREMIUM 3D CAROUSEL (Fixed Navigation Visibility)
    Cinematic Depth | Glassmorphism | Smooth Physics
--------------------------------------------------------------------------- */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, ArrowUpRight } from "lucide-react";

const cn = (...classes) => classes.filter(Boolean).join(" ");

/* --- 1. SLIDE CONTENT (UNCHANGED) --- */
const slides = [
    {
        id: 1,
        type: "video",
        content: (
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#0F0F0F] cursor-pointer group ring-1 ring-white/10">
                <video
                    id="video-1"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 ease-out"
                    src="/framelync_intro.mp4"
                    preload="metadata"
                    muted
                    loop
                    playsInline
                    controlsList="nofullscreen nodownload"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                         <span className="text-xs font-bold uppercase tracking-widest">Watch</span>
                         <ArrowUpRight size={16} />
                    </div>
                </div>
            </div>
        ),
    },
    {
        id: 2,
        type: "video",
        content: (
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#0F0F0F] cursor-pointer group ring-1 ring-white/10">
                <video
                    id="video-2"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 ease-out"
                    src="/dhatri_fin_services__explainer.mp4"
                    preload="metadata"
                    muted
                    loop
                    playsInline
                    controlsList="nofullscreen nodownload"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                         <span className="text-xs font-bold uppercase tracking-widest">Watch</span>
                         <ArrowUpRight size={16} />
                    </div>
                </div>
            </div>
        ),
    },
    {
        id: 3,
        type: "video",
        content: (
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#0F0F0F] cursor-pointer group ring-1 ring-white/10">
                <video
                    id="video-3"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 ease-out"
                    src="/logo-animation-ryich-delicacies.mp4"
                    preload="metadata"
                    muted
                    loop
                    playsInline
                    controlsList="nofullscreen nodownload"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                         <span className="text-xs font-bold uppercase tracking-widest">Watch</span>
                         <ArrowUpRight size={16} />
                    </div>
                </div>
            </div>
        ),
    },
    {
        id: 4,
        type: "video",
        content: (
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#0F0F0F] cursor-pointer group ring-1 ring-white/10">
                <video
                    id="video-4"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 ease-out"
                    src="/logo_animation_wired_learning.mp4"
                    preload="metadata"
                    muted
                    loop
                    playsInline
                    controlsList="nofullscreen nodownload"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                         <span className="text-xs font-bold uppercase tracking-widest">Watch</span>
                         <ArrowUpRight size={16} />
                    </div>
                </div>
            </div>
        ),
    },
    {
        id: 5,
        type: "video",
        content: (
            <div className="relative w-full h-full rounded-2xl overflow-hidden bg-[#0F0F0F] cursor-pointer group ring-1 ring-white/10">
                <video
                    id="video-5"
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700 ease-out"
                    src="/social_platform.mp4"
                    preload="metadata"
                    muted
                    loop
                    playsInline
                    controlsList="nofullscreen nodownload"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                         <span className="text-xs font-bold uppercase tracking-widest">Watch</span>
                         <ArrowUpRight size={16} />
                    </div>
                </div>
            </div>
        ),
    },
];

const TransparentCarousel = () => {
    // --- STATE & LOGIC ---
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
    const animationTimeoutRef = useRef(null);
    const slideRefs = useRef([]);
    const containerRef = useRef(null);
    const isHoveredRef = useRef(false);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [carouselHeight, setCarouselHeight] = useState("400px");
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const loadedCountRef = useRef(0);
    const videoRefs = useRef({});

    const getCarouselHeight = useCallback(() => {
        if (windowWidth < 640) return "240px";
        if (windowWidth < 768) return "280px";
        if (windowWidth < 1024) return "320px";
        if (windowWidth < 1280) return "360px";
        return "400px";
    }, [windowWidth]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setCarouselHeight(getCarouselHeight());
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, [getCarouselHeight]);

    useEffect(() => {
        const videoLoadHandlers = [];
        const loadImages = () => {
            let loaded = 0;
            const totalImages = slides.length;
            const onImageLoad = () => {
                loaded++;
                loadedCountRef.current = loaded;
                if (loaded === totalImages) setImagesLoaded(true);
            };
            slides.forEach((slide) => {
                const videoElement = document.getElementById(`video-${slide.id}`);
                if (videoElement) {
                    videoElement.addEventListener('loadeddata', onImageLoad);
                    videoLoadHandlers.push([videoElement, onImageLoad]);
                    videoRefs.current[slide.id] = videoElement;
                    videoElement.play().catch(e => console.warn("Autoplay prevented", e));
                    if (videoElement.readyState >= 2) onImageLoad();
                } else {
                    onImageLoad();
                }
            });
        };
        loadImages();
        return () => {
            videoLoadHandlers.forEach(([videoElement, handler]) => {
                videoElement.removeEventListener('loadeddata', handler);
            });
        };
    }, []);

    const handleNext = useCallback((manual = false) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % slides.length);
        if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = setTimeout(() => setIsAnimating(false), 600);
    }, [isAnimating]);

    const handlePrev = useCallback((manual = false) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
        if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = setTimeout(() => setIsAnimating(false), 600);
    }, [isAnimating]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowLeft") handlePrev(true);
            else if (e.key === "ArrowRight") handleNext(true);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleNext, handlePrev]);

    useEffect(() => () => { if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current); }, []);

    // --- RESPONSIVE LOGIC ---
    const getResponsiveX = (position) => {
        const baseFactor = 0.85;
        let factor = baseFactor;
        let additionalOffset = 0;

        if (windowWidth < 640) factor = 1.35; 
        else if (windowWidth < 768) factor = 1.3;
        else if (windowWidth < 1024) factor = 1.0;
        else if (windowWidth >= 1200) additionalOffset = 250;

        const calculatedOffset = 98 * factor;
        const calculatedFarOffset = 200 * factor;

        switch (position) {
            case "center": return 0;
            case "left": return `-${calculatedOffset + additionalOffset}px`;
            case "right": return `${calculatedOffset + additionalOffset}px`;
            case "farLeft": return `-${calculatedFarOffset + (additionalOffset * 2)}px`;
            case "farRight": return `${calculatedFarOffset + (additionalOffset * 2)}px`;
            default: return 0;
        }
    };

    const getSlideMaxWidth = () => {
        if (windowWidth < 640) return "85%";
        if (windowWidth < 768) return "calc(100% - 60px)";
        if (windowWidth < 1024) return "70%";
        if (windowWidth < 1280) return "55%";
        return "45%";
    };

    const getSlidePosition = (index) => {
        if (index === currentIndex) return "center";
        else if ((currentIndex === 0 && index === slides.length - 1) || index === currentIndex - 1) return "left";
        else if ((currentIndex === slides.length - 1 && index === 0) || index === currentIndex + 1) return "right";
        else {
            const distanceRight = (index - currentIndex + slides.length) % slides.length;
            const distanceLeft = (currentIndex - index + slides.length) % slides.length;
            if (distanceLeft === 2) return "farLeft";
            if (distanceRight === 2) return "farRight";
            return "hidden";
        }
    };

    const transitionPhysics = {
        type: "spring",
        stiffness: 260,
        damping: 30,
        mass: 1,
        restDelta: 0.001
    };

    const variants = {
        center: {
            x: 0,
            // UPDATED: Changed from 20 to 10 (half pulled up)
            y: windowWidth < 640 ? 10 : 0, 
            scale: 1,
            zIndex: 10,
            opacity: 1,
            filter: "brightness(1) blur(0px)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            transition: transitionPhysics,
        },
        left: {
            x: getResponsiveX("left"),
            y: 15,
            scale: 0.85,
            zIndex: 5,
            opacity: 0.6,
            filter: "brightness(0.5) blur(2px)",
            transition: transitionPhysics,
        },
        right: {
            x: getResponsiveX("right"),
            y: 15,
            scale: 0.85,
            zIndex: 5,
            opacity: 0.6,
            filter: "brightness(0.5) blur(2px)",
            transition: transitionPhysics,
        },
        farLeft: {
            x: getResponsiveX("farLeft"),
            y: 30,
            scale: 0.7,
            zIndex: 1,
            opacity: 0,
            filter: "brightness(0.3) blur(5px)",
            transition: transitionPhysics,
        },
        farRight: {
            x: getResponsiveX("farRight"),
            y: 30,
            scale: 0.7,
            zIndex: 1,
            opacity: 0,
            filter: "brightness(0.3) blur(5px)",
            transition: transitionPhysics,
        },
        hidden: (direction) => ({
            x: direction >= 0 ? "100%" : "-100%",
            y: 40,
            scale: 0.5,
            opacity: 0,
            transition: { duration: 0.3 },
        }),
        exit: (direction) => ({
            x: direction > 0 ? "-100%" : "100%",
            y: 40,
            scale: 0.5,
            opacity: 0,
            transition: { duration: 0.3 },
        }),
    };

    const handleDragEnd = (e, { offset, velocity }) => {
        if (offset.x < -15 || (velocity.x < -0.2 && offset.x < 0)) handleNext(true);
        else if (offset.x > 15 || (velocity.x > 0.2 && offset.x > 0)) handlePrev(true);
    };

    // --- BUTTONS VISIBLE ON ALL SCREENS ---
    const arrowContainerStyle = () => {
        return {
            display: 'flex',
            gap: '1rem',
            zIndex: 10,
            marginTop: '2rem', 
            justifyContent: 'center',
            position: 'relative', 
            width: '100%',
        };
    };

    const handleVideoClick = (index) => {
        if(index !== currentIndex) return;
        const videoElement = videoRefs.current[index + 1];
        if (videoElement) {
            setPlayingVideo(index);
            Object.values(videoRefs.current).forEach((vid) => {
                if (vid !== videoElement) vid.pause();
            });
            videoElement.pause();
        }
    };

    const handleCloseVideo = () => {
        setPlayingVideo(null);
        Object.values(videoRefs.current).forEach((vid) => {
            vid.play().catch(e => console.warn("Autoplay prevented on close", e));
        });
    };

    return (
        <div
            className="relative w-full max-w-[90rem] mx-auto py-12 px-2 bg-transparent overflow-hidden"
            ref={containerRef}
            onMouseEnter={() => (isHoveredRef.current = true)}
            onMouseLeave={() => (isHoveredRef.current = false)}
        >
            <div
                className="relative flex justify-center items-center w-full perspective-[1000px]"
                style={{ height: carouselHeight }}
            >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    {slides.map((slide, index) => {
                        const position = getSlidePosition(index);
                        return position !== "hidden" ? (
                            <motion.div
                                key={slide.id}
                                ref={(el) => (slideRefs.current[index] = el)}
                                custom={direction}
                                variants={variants}
                                initial="hidden"
                                animate={position}
                                exit="exit"
                                drag={!isAnimating ? "x" : false}
                                dragConstraints={containerRef.current ? {
                                    left: -containerRef.current.offsetWidth / 2,
                                    right: containerRef.current.offsetWidth / 2,
                                } : { left: 0, right: 0 }}
                                dragElastic={0.08}
                                onDragEnd={handleDragEnd}
                                className="absolute rounded-2xl overflow-hidden cursor-pointer shadow-2xl"
                                style={{
                                    width: getSlideMaxWidth(),
                                    aspectRatio: "16/9",
                                    transform: "translateZ(0)",
                                    backfaceVisibility: "hidden",
                                    touchAction: "pan-y"
                                }}
                                onClick={() => { if (slide.type === 'video') handleVideoClick(index); }}
                            >
                                {slide.content}
                            </motion.div>
                        ) : null;
                    })}
                </AnimatePresence>
                
                {!imagesLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10">
                             <div className="w-2 h-2 bg-white rounded-full animate-pulse"/>
                             <span className="text-xs font-bold uppercase tracking-widest text-white/50">Loading Assets</span>
                        </div>
                    </div>
                )}
            </div>
            
            {/* NAVIGATION CONTROLS */}
            <div style={arrowContainerStyle()}>
                <motion.button
                    onClick={() => handlePrev(true)}
                    className={cn(
                        "w-12 h-12 flex items-center justify-center rounded-full border border-[#0F0F0F]/10",
                        "bg-white/50 backdrop-blur-md text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition-all duration-300 shadow-lg",
                        isAnimating && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={isAnimating || !imagesLoaded}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <ChevronLeft size={20} />
                </motion.button>
                <motion.button
                    onClick={() => handleNext(true)}
                    className={cn(
                        "w-12 h-12 flex items-center justify-center rounded-full border border-[#0F0F0F]/10",
                        "bg-white/50 backdrop-blur-md text-[#0F0F0F] hover:bg-[#0F0F0F] hover:text-white transition-all duration-300 shadow-lg",
                        isAnimating && "opacity-50 cursor-not-allowed"
                    )}
                    disabled={isAnimating || !imagesLoaded}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <ChevronRight size={20} />
                </motion.button>
            </div>

            {/* CINEMATIC LIGHTBOX MODAL */}
            <AnimatePresence>
                {playingVideo !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        onClick={handleCloseVideo}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 bg-black/60 backdrop-blur-md"
                    >
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="relative w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
                            style={{ aspectRatio: "16/9", maxHeight: '80vh' }}
                            onClick={(e) => e.stopPropagation()} 
                        >
                            <video
                                id={`video-${playingVideo + 1}`}
                                className="w-full h-full object-contain bg-black"
                                src={slides[playingVideo].type === 'video' ? (document.getElementById(`video-${playingVideo + 1}`)?.src || '') : ''}
                                muted={false}
                                autoPlay
                                controls
                                playsInline
                                controlsList="nofullscreen nodownload"
                            />
                            
                            <button
                                onClick={handleCloseVideo}
                                className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 bg-black/50 hover:bg-[#0F0F0F] text-white backdrop-blur-md rounded-full border border-white/10 transition-all duration-300 group"
                            >
                                <span className="text-[10px] font-bold tracking-widest uppercase hidden sm:block">Close</span>
                                <X size={16} className="group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TransparentCarousel;